/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบเลือกวัสดุของลูกค้า   /support/selection/manage/:code
   Support เปิดบนแท็บเล็ต/จอ พาลูกค้าเลือกทีละจุด (เห็นรูป + ส่วนต่างราคา)
   เลือกครบแล้ว → ลูกค้าเซ็นในจอ → ล็อกสเปก (แก้ไม่ได้ ต้องทำใบเปลี่ยนแปลงภายหลัง)
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import { CheckCircleFilled, DeleteOutlined, LockOutlined, PlusOutlined, PrinterOutlined, SaveFilled, StopOutlined, UnlockOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ButtonBack } from "../../../components/button";
import StatusTag from "../../../components/ak/StatusTag";
import SignaturePad from "../../../components/ak/SignaturePad";
import { SelectionService, apiError } from "../../../service/Ak.service";
import OptionService from "../../../service/Options.service";
import { usePermission } from "../../../store/context/permission-context";
import { fileUrl, formatMoney } from "../../../utils/util";

const opt = OptionService();
const money = { formatter: (v) => (v === undefined || v === null || v === "" ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")), parser: (v) => `${v}`.replace(/,/g, "") };
const baht = (n) => `${Number(n) > 0 ? "+" : ""}${formatMoney(n, 0)}`;

export default function SelectionManage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { can, isAdmin } = usePermission();
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [remark, setRemark] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mats, setMats] = useState([]);
  const [lockOpen, setLockOpen] = useState(false);
  const [signer, setSigner] = useState("");
  const [signature, setSignature] = useState(null);
  const [locking, setLocking] = useState(false);
  const [area, setArea] = useState("ทั้งหมด");

  const editable = sel?.status === "ร่าง" && can("support_selection", "edit");

  const load = () =>
    SelectionService.get(code)
      .then((res) => {
        const s = res.data?.data;
        setSel(s);
        setItems((s.items || []).map((it) => ({ ...it, key: `i${it.id}` })));
        setRemark(s.remark || "");
        setDirty(false);
      })
      .catch((e) => message.error(apiError(e, "โหลดใบเลือกวัสดุไม่สำเร็จ")));

  useEffect(() => {
    load();
    opt.materials().then((r) => setMats(r.data?.data || []));
  }, [code]);

  /* กันปิดหน้าทั้งที่ยังไม่บันทึก */
  useEffect(() => {
    const h = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const setItem = (key, patch) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const choose = (it, o) => {
    if (!editable) return;
    setItem(it.key, {
      mat_code: o.mat_code,
      option_type: o.option_type,
      price_diff: o.price_diff,
      mat_name: o.mat_name,
      brand: o.brand,
      model: o.model,
      color: o.color,
      image_path: o.image_path,
    });
  };

  const addCustom = () => {
    setItems((prev) => [...prev, { key: `n${Date.now()}`, tpl_item_id: null, area: area !== "ทั้งหมด" ? area : null, item_name: "", qty: 1, unit: "ชุด", price_diff: 0, mat_code: null, option_type: "upgrade", options: [] }]);
    setDirty(true);
  };

  const total = useMemo(() => items.reduce((a, it) => a + Number(it.price_diff || 0), 0), [items]);
  const upgrades = items.filter((it) => it.option_type === "upgrade").length;
  const missing = items.filter((it) => it.tpl_item_id && !it.mat_code);
  const areas = useMemo(() => ["ทั้งหมด", ...Array.from(new Set(items.map((it) => it.area || "อื่น ๆ")))], [items]);
  const shown = items.filter((it) => area === "ทั้งหมด" || (it.area || "อื่น ๆ") === area);

  const payload = () => ({
    sel_code: code,
    remark,
    items: items.map((it) => ({
      id: it.id,
      mat_code: it.mat_code,
      note: it.note,
      ...(it.tpl_item_id ? {} : { area: it.area, item_name: it.item_name, cat_code: it.cat_code, qty: it.qty, unit: it.unit, price_diff: it.price_diff }),
    })),
  });

  const save = async (silent = false) => {
    const bad = items.find((it) => !it.tpl_item_id && !String(it.item_name || "").trim());
    if (bad) {
      message.error("รายการพิเศษต้องมีชื่อรายการ");
      return false;
    }
    setSaving(true);
    try {
      await SelectionService.update(payload());
      if (!silent) message.success("บันทึกแล้ว");
      await load();
      return true;
    } catch (e) {
      message.error(apiError(e, "บันทึกไม่สำเร็จ"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openLock = async () => {
    if (missing.length) return message.warning(`ยังไม่ได้เลือก: ${missing.map((m) => m.item_name).join(", ")}`);
    if (dirty && !(await save(true))) return;
    setSigner([sel.prename, sel.cus_name].filter(Boolean).join(" "));
    setSignature(null);
    setLockOpen(true);
  };

  const lock = async () => {
    if (!signer.trim()) return message.warning("กรอกชื่อผู้ลงนาม");
    if (!signature) return message.warning("ให้ลูกค้าเซ็นชื่อก่อน");
    setLocking(true);
    try {
      await SelectionService.patch({ sel_code: code, action: "lock", signer_name: signer, signature });
      message.success("ล็อกสเปกเรียบร้อย");
      setLockOpen(false);
      load();
    } catch (e) {
      message.error(apiError(e, "ล็อกสเปกไม่สำเร็จ"));
    } finally {
      setLocking(false);
    }
  };

  const unlock = () =>
    Modal.confirm({
      title: "ปลดล็อกใบเลือกวัสดุ?",
      content: "ลายเซ็นลูกค้าจะถูกล้าง และต้องให้ลูกค้าเซ็นใหม่หลังแก้ไข",
      okText: "ปลดล็อก",
      cancelText: "ไม่",
      onOk: () => SelectionService.patch({ sel_code: code, action: "unlock" }).then(load).catch((e) => message.error(apiError(e))),
    });

  const cancelSel = () =>
    Modal.confirm({
      title: "ยกเลิกใบเลือกวัสดุนี้?",
      okText: "ยกเลิกใบ",
      okButtonProps: { danger: true },
      cancelText: "ไม่",
      onOk: () => SelectionService.remove(code).then(load).catch((e) => message.error(apiError(e))),
    });

  if (!sel) return <Flex justify="center" style={{ padding: 80 }}><Spin /></Flex>;

  const OptionTile = ({ it, o }) => {
    const active = it.mat_code === o.mat_code;
    return (
      <div className={`ak-option ${active ? "active" : ""} ${editable ? "clickable" : ""}`} onClick={() => choose(it, o)}>
        <div className="ak-option-img">
          {o.image_path ? <img src={fileUrl(o.image_path)} alt={o.mat_name} /> : <span>ไม่มีรูป</span>}
          {active && <CheckCircleFilled className="ak-option-check" />}
        </div>
        <div className="ak-option-body">
          <div className="ak-option-name">{o.mat_name}</div>
          <div className="ak-option-sub">{[o.brand, o.model, o.color].filter(Boolean).join(" · ")}</div>
          <div className={`ak-option-price ${o.option_type}`}>{o.option_type === "standard" ? "มาตรฐาน (รวมในราคา)" : `อัปเกรด ${baht(o.price_diff)} บาท`}</div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Flex justify="space-between" align="center" className="mb-3" wrap="wrap" gap={8}>
        <div>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            ใบเลือกวัสดุ {sel.sel_code} <StatusTag value={sel.status} />
          </Typography.Title>
          <Typography.Text type="secondary">
            <Typography.Link onClick={() => navigate(`/support/project/view/${sel.project_code}`)}>{sel.project_name}</Typography.Link>
            {` · ${[sel.prename, sel.cus_name].filter(Boolean).join(" ")} · ${sel.tpl_name || ""}`}
          </Typography.Text>
        </div>
        <Space wrap>
          <ButtonBack target="/support/selection" />
          <Button icon={<PrinterOutlined />} onClick={() => window.open(`/print/selection/${code}`, "_blank")}>
            พิมพ์
          </Button>
          {sel.status === "ร่าง" && can("support_selection", "delete") && (
            <Button danger icon={<StopOutlined />} onClick={cancelSel}>
              ยกเลิกใบ
            </Button>
          )}
          {sel.status === "ล็อกสเปกแล้ว" && isAdmin && (
            <Button icon={<UnlockOutlined />} onClick={unlock}>
              ปลดล็อก
            </Button>
          )}
          {editable && (
            <>
              <Button icon={<SaveFilled />} loading={saving} onClick={() => save()} type={dirty ? "primary" : "default"}>
                {dirty ? "บันทึก*" : "บันทึก"}
              </Button>
              <Button type="primary" icon={<LockOutlined />} onClick={openLock}>
                ลูกค้ายืนยัน / ล็อกสเปก
              </Button>
            </>
          )}
        </Space>
      </Flex>

      {sel.status === "ล็อกสเปกแล้ว" && (
        <Alert
          className="mb-3"
          type="success"
          showIcon
          message={`ล็อกสเปกแล้วเมื่อ ${dayjs(sel.locked_date).format("DD/MM/YYYY HH:mm")} — ลงนามโดย ${sel.signer_name}`}
          description="ถ้าลูกค้าต้องการเปลี่ยน ต้องทำใบเปลี่ยนแปลงงาน (CO) — เฟสถัดไป"
        />
      )}
      {sel.status === "ยกเลิก" && <Alert className="mb-3" type="error" showIcon message="ใบนี้ถูกยกเลิกแล้ว" />}

      <Row gutter={[16, 16]} className="mb-3">
        <Col xs={12} md={6}>
          <Card size="small" className="ak-stat"><Statistic title="จุดที่เลือก" value={items.length} suffix="จุด" /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" className="ak-stat"><Statistic title="อัปเกรด" value={upgrades} suffix="รายการ" /></Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" className="ak-stat ak-stat-total">
            <Statistic title="ส่วนต่างที่ลูกค้าจ่ายเพิ่ม" value={total} precision={2} suffix="บาท" />
          </Card>
        </Col>
      </Row>

      <Flex gap={6} wrap="wrap" className="mb-3">
        {areas.map((a) => (
          <Button key={a} size="small" type={area === a ? "primary" : "default"} onClick={() => setArea(a)}>
            {a}
          </Button>
        ))}
      </Flex>

      {shown.length === 0 && <Card><Empty /></Card>}

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {shown.map((it) =>
          it.tpl_item_id ? (
            <Card
              key={it.key}
              size="small"
              className="ak-card"
              title={
                <Space wrap>
                  {it.area && <Tag>{it.area}</Tag>}
                  <span>{it.item_name}</span>
                  {it.qty ? <Typography.Text type="secondary">{`${formatMoney(it.qty, Number(it.qty) % 1 ? 2 : 0)} ${it.unit || ""}`}</Typography.Text> : null}
                  {!it.mat_code && <Badge status="error" text="ยังไม่ได้เลือก" />}
                </Space>
              }
              extra={it.option_type === "upgrade" ? <Tag color="gold">{baht(it.price_diff)} บาท</Tag> : it.mat_code ? <Tag color="green">มาตรฐาน</Tag> : null}
            >
              <div className="ak-option-grid">
                {(it.options || []).map((o) => (
                  <OptionTile key={o.option_id} it={it} o={o} />
                ))}
              </div>
              <Input
                className="mt-2"
                placeholder="หมายเหตุของจุดนี้ (เช่น ลูกค้าขอดูของจริงก่อน)"
                value={it.note || ""}
                disabled={!editable}
                maxLength={255}
                onChange={(e) => setItem(it.key, { note: e.target.value })}
              />
            </Card>
          ) : (
            <Card key={it.key} size="small" className="ak-card ak-custom" title={<Space><Tag color="purple">รายการพิเศษ</Tag>{it.item_name || "ยังไม่ตั้งชื่อ"}</Space>}
              extra={editable && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => { setItems((p) => p.filter((x) => x.key !== it.key)); setDirty(true); }} />}>
              <Row gutter={8}>
                <Col xs={24} md={4}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>ห้อง / พื้นที่</Typography.Text>
                  <Input disabled={!editable} value={it.area} maxLength={100} onChange={(e) => setItem(it.key, { area: e.target.value })} />
                </Col>
                <Col xs={24} md={6}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>รายการ *</Typography.Text>
                  <Input disabled={!editable} value={it.item_name} maxLength={200} onChange={(e) => setItem(it.key, { item_name: e.target.value })} placeholder="เช่น ประตูรั้วรีโมท" />
                </Col>
                <Col xs={24} md={6}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>วัสดุ (ถ้ามีในทะเบียน)</Typography.Text>
                  <Select disabled={!editable} allowClear showSearch optionFilterProp="label" value={it.mat_code} style={{ width: "100%" }}
                    options={mats.map((m) => ({ value: m.mat_code, label: m.label }))} onChange={(v) => setItem(it.key, { mat_code: v || null })} />
                </Col>
                <Col xs={8} md={2}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>จำนวน</Typography.Text>
                  <InputNumber disabled={!editable} value={it.qty} min={0} style={{ width: "100%" }} onChange={(v) => setItem(it.key, { qty: v })} />
                </Col>
                <Col xs={8} md={2}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>หน่วย</Typography.Text>
                  <Input disabled={!editable} value={it.unit} maxLength={30} onChange={(e) => setItem(it.key, { unit: e.target.value })} />
                </Col>
                <Col xs={8} md={4}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>ราคาเพิ่ม (บาท)</Typography.Text>
                  <InputNumber disabled={!editable} value={it.price_diff} style={{ width: "100%" }} {...money} onChange={(v) => setItem(it.key, { price_diff: v || 0 })} />
                </Col>
              </Row>
              <Input className="mt-2" placeholder="หมายเหตุ" value={it.note || ""} disabled={!editable} maxLength={255} onChange={(e) => setItem(it.key, { note: e.target.value })} />
            </Card>
          )
        )}
      </Space>

      {editable && (
        <Button className="mt-3" icon={<PlusOutlined />} onClick={addCustom}>
          เพิ่มรายการพิเศษ (นอก Template)
        </Button>
      )}

      <Card className="ak-card mt-3" title="หมายเหตุของใบ">
        <Input.TextArea rows={3} value={remark} disabled={!editable} maxLength={2000} onChange={(e) => { setRemark(e.target.value); setDirty(true); }} />
      </Card>

      {sel.signature_path && (
        <Card className="ak-card mt-3" title="ลายเซ็นลูกค้า">
          <Space direction="vertical">
            <Image src={fileUrl(sel.signature_path)} height={120} style={{ background: "#fff", border: "1px solid #eee", borderRadius: 6 }} />
            <Typography.Text>{sel.signer_name}</Typography.Text>
            <Typography.Text type="secondary">
              {dayjs(sel.locked_date).format("DD/MM/YYYY HH:mm")} · ผู้บันทึก {sel.locked_by_name || "-"}
            </Typography.Text>
          </Space>
        </Card>
      )}

      <Modal open={lockOpen} title="ลูกค้ายืนยันสเปก / ล็อกสเปก" onCancel={() => setLockOpen(false)} onOk={lock} okText="ยืนยันและล็อกสเปก" cancelText="ยกเลิก" confirmLoading={locking} width={640} destroyOnClose>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message={`ส่วนต่างที่ลูกค้าจ่ายเพิ่ม ${formatMoney(total, 2)} บาท (${upgrades} รายการอัปเกรด)`}
            description="เมื่อล็อกแล้วแก้ไขไม่ได้ ถ้าเปลี่ยนภายหลังต้องทำใบเปลี่ยนแปลงงาน"
          />
          <Form layout="vertical">
            <Form.Item label="ชื่อผู้ลงนาม (ลูกค้า)" required>
              <Input value={signer} onChange={(e) => setSigner(e.target.value)} maxLength={200} />
            </Form.Item>
            <Form.Item label="ลายเซ็น" required className="!mb-0">
              <SignaturePad onChange={setSignature} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </div>
  );
}
